/* eslint-disable max-lines */
import type { AccordionItem } from '@zencity/common-survey/lib/content.mjs';
import { SupportedLanguages, TranslatableField } from './supportedLanguages';
import type { MapLine, MapPolygon, PointOfInterest } from './surveyMapLayers';

export interface SurveyLanguage {
  language_code: string;
  display_name: string;
}

export enum LayoutTypes {
  SHORT = 'short',
  LONG = 'long',
  BUTTON = 'button',
  LIST = 'list',
  DROPDOWN = 'dropdown',
  IMAGE = 'image',
  SCALE = 'scale',
  STARS = 'stars',
  RANGE = 'range',
  PIN_IMAGE = 'pin-image',
  PIN_MAP = 'pin-map',
  MAP_DRAW_LINE = 'map_draw_line',
  MAP_DRAW_SHAPE = 'map_draw_shape',
  LEFT = 'left',
  RIGHT = 'right',
  CENTER = 'center',
}

export enum EntityTypes {
  QUESTION = 'question',
  SECTION = 'section',
  CONTENT = 'content',
  LOGIC_JUMP = 'logic_jump',
}

export enum QuestionTypes {
  CHOICES = 'choices_options',
  OPEN_ENDED = 'open_ended',
  NUMBER = 'number',
  SLIDER = 'slider',
  PINDROP = 'pindrop',
  MATRIX = 'matrix',
  RANK = 'rank',
  BUDGET_CLOSED = 'budget_closed',
  BUDGET_OPEN = 'budget_open',
  MEDIA_UPLOAD = 'media_upload',
}

export enum ContentTypes {
  TEXT = 'text',
  IMAGE = 'image',
  VIDEO = 'video',
  IMAGE_SLIDESHOW = 'image_slideshow',
  IMAGE_GRID = 'image_grid',
  QUOTE = 'quote',
  IFRAME = 'iframe',
  NOTIFICATION_SIGNUP = 'notification_signup',
  LINK = 'link',
  DOCUMENT = 'document',
  MIXED_LIST = 'mixed_list',
  ACCORDION = 'accordion',
}

// Hand-rolled mirrors of @zencity/common-survey's LinkItem/DocumentItem — the
// transformed configurations are handed straight to the package's renderers,
// so these shapes must stay in lockstep with its surveyInfo types.
export interface LinkItem {
  label: TranslatableField;
  url: TranslatableField;
}

export interface DocumentItem {
  title: TranslatableField;
  url: TranslatableField;
  description: TranslatableField;
  file_name?: string;
  file_size?: string;
}

// Sentinel pin_type for a custom uploaded SVG marker (markup stored inline in PinColor.pin_svg).
export const CUSTOM_SVG_PIN_TYPE = 'custom_svg';

export interface PinColor {
  inputUuid?: string;
  // Render descriptor: preset color name or 'custom_svg'; pin_svg holds the marker SVG when custom.
  pin_type?: string;
  pin_svg?: string;
  label: TranslatableField;
  limit: number;
  allow_comment: boolean;
}

export interface PinColors {
  [key: string]: PinColor;
}

export enum BudgetCapTypes {
  HARD = 'hard',
  SOFT = 'soft',
}

export interface Configuration extends Record<string, unknown> {
  allow_multi_choice?: boolean;
  display_other?: boolean;
  display_other_id?: string;
  display_other_uuid?: string;
  center?: number[];
  zoom?: number;
  max_selection?: number;
  min_selection?: number;
  pins_colors?: PinColors;
  pins_polygons?: MapPolygon[];
  map_lines?: MapLine[];
  points_of_interest?: PointOfInterest[];
  video_type?: string;
  url?: TranslatableField;
  description?: TranslatableField;
  is_required?: boolean;
  image_pindrop?: TranslatableField;
  step?: number;
  cap?: number;
  cap_type?: BudgetCapTypes;
  cap_units?: TranslatableField;
  randomize?: boolean;
  randomize_rows?: boolean;
  randomize_cols?: boolean;
  display_na?: boolean;
  display_na_id?: string;
  include_not_applicable?: boolean;
  row_multiple_choice?: {
    max: number;
    min: number;
  };
  validation_type?: string;
  width?: number;
  decimal?: number;
  label?: TranslatableField;
  units?: TranslatableField;
  scale_label_first?: TranslatableField;
  scale_label_middle?: TranslatableField;
  scale_label_last?: TranslatableField;
  enforce_budget_cap?: boolean;
  budget_cap?: number;
  comparison_display_type?: string;
  disable_comments?: boolean;
  restrict_pins_in_polygons?: boolean;
  // The map's named pin types as the resident filter lists them (ENG-4449).
  pin_types?: { id: string; label: TranslatableField }[];
  // Admin's "Enable Filters" toggle on a pin-drop map question.
  enable_pin_filters?: boolean;
  single_pin_mode?: boolean;
  total_pin_limit?: number;
  limit_submissions_for_resident?: number;
  map_layers?: unknown[];
  links?: LinkItem[];
  documents?: DocumentItem[];
  // Imported rather than mirrored: common-survey exports its accordion data contract from lib/content.
  accordion_items?: AccordionItem[];
  // Layout variant of a links block (content display type from QBB): 'link_list' | 'link_grid'.
  layout?: string;
}

interface QuestionChoiceConfiguration {
  is_non_substantiative: boolean;
  numeric_value?: number;
}

export interface QuestionChoice {
  id: string;
  uuid: string;
  text: TranslatableField;
  image?: TranslatableField;
  altText?: TranslatableField;
  description?: TranslatableField;
  configurations?: QuestionChoiceConfiguration;
  amount?: number;
  mandatory?: boolean;
  min_selection?: number;
  max_selection?: number;
  step?: number;
  default?: number;
  comparison?: number;
  exclusive?: boolean;
}

export interface Question {
  id: string;
  uuid: string;
  index: number;
  entity_type: EntityTypes.QUESTION;
  question_type: QuestionTypes;
  layout_type?: LayoutTypes;
  text: TranslatableField;
  pageTitle?: TranslatableField;
  description?: TranslatableField;
  choices?: QuestionChoice[];
  rows?: QuestionChoice[];
  columns?: QuestionChoice[];
  configurations: Configuration;
  inputs?: { id?: string; uuid: string; value: string; index: string | number }[];
}

export interface Content {
  id: string;
  uuid?: string;
  index?: number;
  entity_type: EntityTypes.CONTENT;
  content_type: ContentTypes;
  text?: TranslatableField;
  description?: TranslatableField;
  configurations: Configuration;
  metadata?: Record<string, unknown>;
}

export interface Section {
  id: string;
  uuid?: string;
  index: number;
  entity_type: EntityTypes.SECTION;
  text: TranslatableField;
  sectionEntities: (Question | Content)[];
  configurations: Configuration;
}

export declare enum Operator {
  IS = 'is',
  IS_NOT = 'is_not',
  GT = 'gt',
  GTE = 'gte',
  LT = 'lt',
  LTE = 'lte',
  EQ = 'eq',
  NEQ = 'neq',
  EQUALS = 'equals',
  NOT_EQUALS = 'not_equals',
  CONTAINS = 'contains',
  NOT_CONTAINS = 'not_contains',
  STARTS_WITH = 'starts_with',
  END_WITH = 'end_with',
}
export declare enum DestinationType {
  QUESTION = 'question',
  END_SCREEN = 'end_screen',
  WELCOME_SCREEN = 'welcome_screen',
}
export declare enum Conjunction {
  AND = 'AND',
  OR = 'OR',
}
export declare enum AnswerProperty {
  VALUE = 'value',
  SELECTED_CHOICES = 'selected_choices',
}
export interface LogicJumpCondition {
  operand_a_origin: {
    origin_question_id: number;
    answer_property: AnswerProperty;
  };
  operator: Operator;
  operand_b: string | number;
}
interface LogicJumpExpressionBase {
  conjunction: Conjunction;
}
interface LogicJumpExpressionWithConditions extends LogicJumpExpressionBase {
  conditions: LogicJumpCondition[];
}
interface LogicJumpExpressionWithParenthesis extends LogicJumpExpressionBase {
  // eslint-disable-next-line no-use-before-define
  parenthesis: LogicJumpExpression[];
}
export type LogicJumpExpression = LogicJumpExpressionWithConditions | LogicJumpExpressionWithParenthesis;
export interface LogicJumpRule {
  index?: number;
  always?: boolean;
  expression: LogicJumpExpression;
  destination: {
    type: DestinationType;
    destination_id: string;
  };
}
export interface LogicJump {
  id: string;
  uuid?: string;
  index?: number;
  entity_type: EntityTypes.LOGIC_JUMP;
  logic_jump_rules: LogicJumpRule[];
}

export type EntityType = Question | Section | Content | LogicJump;

export interface WelcomeScreen {
  id: string;
  uuid?: string;
  text: TranslatableField;
  description?: TranslatableField;
  image_url?: TranslatableField;
  sectionEntities?: Content[];
}

export enum EndScreenTypes {
  COMPLETION = 'completion',
  DISQUALIFIED = 'disqualified',
}

export interface EndScreen {
  id: string;
  uuid?: string;
  end_screen_type: EndScreenTypes;
  text?: TranslatableField;
  description?: TranslatableField;
  url_to_redirect?: string;
  delay_redirect: number;
  sectionEntities?: Content[];
  metadata?: {
    hide_signup_fallback?: boolean;
    [key: string]: unknown;
  };
}

export interface EndScreens {
  [key: string]: EndScreen;
}

export enum SurveyTypes {
  LIVE = 'live',
  ONLINE = 'online',
}

export interface ClientInfo {
  id: string;
  name: string;
  country?: string;
}

export interface Language {
  language_code: SupportedLanguages;
  display_name: string;
}

// Legacy survey structure for backward compatibility
export interface LegacySurveyStructure {
  id: string;
  /** The QB questionnaire's own id; on engagement payloads `id` is the EAS engagement uuid. */
  questionnaire_id?: string;
  survey_group_id?: number;
  survey_type: string;
  surveyEntities?: EntityType[]; // legacy property in published payloads
  survey_entities: EntityType[]; // new property (renamed from surveyEntities)
  welcome_screen?: WelcomeScreen;
  end_screens?: EndScreens;
  /** Ids of the pages behind the questionnaire's login gate; absent when it has none. */
  login_gated_page_ids?: string[];
  ab_test_data?: unknown;
}

export interface SurveyType {
  client: ClientInfo;
  is_engage: boolean;
  external_preview_code?: string;
  external_preview_expires_at?: string;
  is_preview?: boolean;
  is_closed?: boolean;
  display_legacy_rfa?: boolean;
  enable_social_share?: boolean;
  version?: string;
  publishVersion?: number; // legacy property in published payloads
  publish_version: number; // transformed property (renamed from publishVersion)
  kiosk_mode?: boolean;
  noindex?: boolean;
  type?: SurveyTypes;
  title_for_display: TranslatableField;
  configuration: {
    description?: TranslatableField;
    image?: TranslatableField;
    exit_button_text?: TranslatableField;
    exit_redirect?: TranslatableField;
    /** @deprecated Ignored: EPF always collects the fingerprint (see screens/survey/fingerprintPolicy). */
    fingerprint_enabled?: boolean;
    /** @deprecated Ignored: the organization's consent toggle decides whether the consent screen shows. */
    fingerprint_request_consent?: boolean;
  };
  language?: SupportedLanguages | string;
  default_language?: string;
  fallback_language?: SupportedLanguages | string;
  languages: SurveyLanguage[];
  structure:
    | {
        id: string;
        questionnaire_id?: string;
        surveyEntities: EntityType[]; // legacy property in published payloads
        survey_entities: EntityType[]; // new property (renamed from surveyEntities)
        welcome_screen?: WelcomeScreen;
        end_screens?: EndScreens;
        login_gated_page_ids?: LegacySurveyStructure['login_gated_page_ids'];
      }
    | LegacySurveyStructure;
  start_time?: string;
  end_time?: string;
  // `true` marks the engagement as authored under the v2 (editable block)
  // notification-signup model. Absent OR `false` => legacy engagement, eligible
  // for the hardcoded signup fallback banner. The legacy gate is `!== true`.
  notification_signup_v2?: boolean;
  // When true, residents may paste into text inputs. Absent/false blocks paste.
  // Sourced from the questionnaire `metadata.paste_enabled` flag; the runtime key
  // must match RFA's `SurveyInfo.paste_enabled` (Survey is passed this object as-is).
  paste_enabled?: boolean;
}
